import { get, writable } from "svelte/store";
import moment from "moment";
import mapboxgl from "mapbox-gl";

import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import TimeSliderData from "../UI/TimeSlider/TimeSliderData";

import WeatherDataSource from "./WeatherDataSource";
import API from "../API/API";
import StringHelper from "../Helpers/StringHelper";
import RangeRing from "../UI/MeasureTool/RangeRing/RangeRing";

import roundFloat from "../Helpers/Misc/roundFloat";
import Analytics from "../Analytics/Analytics";
import AuthStore from "../Auth/Store";
import MapLayer from "../Map/Enums/MapLayer";
import MapDataSource from "../Map/Enums/MapDataSource";
import { Layout, Paint } from "../Map/MapLayerStyles";
import MapLayerManager from "../Map/MapLayerManager";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapStore from "../Map/Store";

const WeatherStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		progress: writable(0),
		error: writable(""),
		lastUpdated: writable(0),
		weather: {
			properties: [
				"windDirection", "windSpeed", "airTemperature", "pressure", "cloudCover", "iceCover", "precipitation", "visibility", "waveHeight", "waveDirection", "wavePeriod", "currentSpeed", "currentDirection",
				"windSpeed1000hpa",
				"windSpeed800hpa",
				"windSpeed500hpa",
				"windSpeed200hpa",
				"windDirection1000hpa",
				"windDirection800hpa",
				"windDirection500hpa",
				"windDirection200hpa",
				"airTemperature1000hpa",
				"airTemperature800hpa",
				"airTemperature500hpa",
				"airTemperature200hpa"
			]
		},
		astronomy: {
			properties: ["astronomicalDawn", "astronomicalDusk", "civilDawn", "civilDusk", "moonrise", "moonset", "nauticalDawn", "nauticalDusk", "sunrise", "sunset"]
		},
		tidalExtremes: {
			properties: ["height", "type"]
		},
		mapInitted: writable(false)
	},
	query: {
		useVessel: writable(null),
		lngLat: writable({ lng: 0, lat: 0 }),
		lngLatReadable: writable(""),
		lngLatMarker: new mapboxgl.Marker({ anchor: "center" }),
		lngLatMarkerPopup: new mapboxgl.Popup({ closeButton: false, closeOnMove: true }),
		fromDatetime: writable(new Date()),
		toDatetime: writable(new Date())
	},
	timeSliderData: new TimeSliderData(),
	// results: writable({}),
	// resultCurrent: writable({}),
	results: {
		weather: writable({}),
		astronomy: writable({}),
		tidalExtremes: writable({}),
		elevation: writable({}),
		tideStationMarker: new mapboxgl.Marker({ color: "#3F81CE", anchor: "center" }),
		tideStationMarkerPopup: new mapboxgl.Popup({ closeButton: false, closeOnMove: true }),
		rangeRing: new RangeRing(0, "#000000")
	},
	resultCurrent: {
		weather: writable({}),
		astronomy: writable({}),
		tidalExtremes: writable({})
	},
	actions: {
		initMap: () => {
			if (get(WeatherStore.meta.mapInitted)) {
				return;
			}
			const map = MapStore.helpers.getMap();
			map.addSource(MapDataSource.ENVIRONMENT_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.ENVIRONMENT_LABELS, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addLayer(
				{
					id: MapLayer.ENVIRONMENT_LINES,
					type: "line",
					source: MapDataSource.ENVIRONMENT_LINES,
					layout: Layout.measure,
					paint: Paint.measure.lines
				}
			);
			map.addLayer(
				{
					id: MapLayer.ENVIRONMENT_LABELS,
					type: "symbol",
					source: MapDataSource.ENVIRONMENT_LABELS,
					layout: Layout.measureText,
					paint: Paint.measure.text
				}
			);
			MapLayerManager.addLayerGroup(MapLayerGroup.ENVIRONMENT, [
				MapLayer.ENVIRONMENT_LINES,
				MapLayer.ENVIRONMENT_LABELS
			], [
				MapDataSource.ENVIRONMENT_LINES,
				MapDataSource.ENVIRONMENT_LABELS
			]);
			WeatherStore.meta.mapInitted.set(true);
		},
		deinitMap: () => {
			if (!get(WeatherStore.meta.mapInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.ENVIRONMENT);
			WeatherStore.meta.mapInitted.set(false);
		},
		clear: () => {
			Analytics.event("reset", { category: "Environment", username: get(AuthStore.userInfo).username });

			WeatherStore.query.useVessel.set(null);
			WeatherStore.query.lngLat.set({ lng: 0, lat: 0 });
			WeatherStore.query.lngLatReadable.set("");
			WeatherStore.query.fromDatetime.set(new Date());
			WeatherStore.query.toDatetime.set(new Date()); // Date.parse(new Date()) + 86400000));
			WeatherStore.query.lngLatMarker.remove();

			WeatherStore.meta.loadState.set(LoadState.INIT);
			WeatherStore.meta.error.set("");
			WeatherStore.meta.lastUpdated.set(Date.now());
		},
		resetCurrent: () => {
			WeatherStore.actions.resetCurrentGeneric("astronomy");
			WeatherStore.actions.resetCurrentGeneric("tidalExtremes");
			WeatherStore.actions.resetCurrentWeather();
		},
		resetCurrentWeather: () => {
			const properties = WeatherStore.meta.weather.properties;
			const current = get(WeatherStore.resultCurrent.weather);
			for (const property in properties) {
				current[properties[property]] = {
					value: "N/A",
					source: WeatherDataSource.NONE
				};
			}
			WeatherStore.resultCurrent.weather.set(current);
		},
		resetCurrentGeneric: (key = "astronomy") => {
			const properties = WeatherStore.meta[key].properties;
			const current = get(WeatherStore.resultCurrent[key]);
			for (const property in properties) {
				current[properties[property]] = {
					value: "N/A"
				};
			}
			WeatherStore.resultCurrent[key].set(current);
		},
		setPropertyInCurrentWeather: (obj, propertyName) => {
			// console.log(obj, propertyName);
			const current = get(WeatherStore.resultCurrent.weather);
			if (typeof obj[propertyName] !== "undefined") {
				const keys = Object.keys(obj[propertyName]);
				// Use Stormglass preferred value if it's there.
				if (keys.indexOf("sg") >= 0) {
					const keyIndex = keys.indexOf("sg");
					const key = keys[keyIndex];
					current[propertyName].value = obj[propertyName][key];
					// We don't want to show Stormglass as the source, so figure it out!
					for (let i = 0; i < keys.length; i++) {
						const iKey = keys[i];
						if (iKey === "sg") {
							continue;
						}
						if (obj[propertyName][iKey] === current[propertyName].value) {
							current[propertyName].source = WeatherDataSource[iKey.toUpperCase()];
							break;
						}
					}

					current[propertyName].options = [];
					for (let i = 0; i < keys.length; i++) {
						if (keys[i] === "sg") {
							continue;
						}
						current[propertyName].options.push({
							value: obj[propertyName][keys[i]],
							source: WeatherDataSource[keys[i].toUpperCase()]
						});
					}
				}
				else {
					const key = keys[0];
					current[propertyName].value = obj[propertyName][key];
					current[propertyName].source = WeatherDataSource[key.toUpperCase()];
					current[propertyName].options = [{
						...current[propertyName]
					}];
				}
			}
			else {
				current[propertyName].value = "N/A";
				current[propertyName].source = WeatherDataSource.NONE;
				current[propertyName].options = [{
					...current[propertyName]
				}];
			}
			WeatherStore.resultCurrent.weather.set(current);
		},
		setCurrentBasedOnTime: (time, updateSlider = false) => {
			WeatherStore.actions.setCurrentGenericBasedOnTime(time, "astronomy");
			WeatherStore.actions.setCurrentGenericBasedOnTime(time, "tidalExtremes");
			WeatherStore.actions.setCurrentWeatherBasedOnTime(time, updateSlider);
		},
		setCurrentGenericBasedOnTime: (time, key = "astronomy") => {
			const results = get(WeatherStore.results[key]);
			const current = get(WeatherStore.resultCurrent[key]);
			const properties = WeatherStore.meta[key].properties;

			if (!results.data) {
				return;
			}

			let index = 0;
			const nowTime = moment.utc(time);
			for (let i = 0; i < results.data.length; i++) {
				const thisTime = moment.utc(results.data[i].time);
				if (thisTime <= nowTime) {
					index = i;
					continue;
				}
				break;
			}
			// console.log("using " + key + " data item index", index);

			const obj = results.data[index];
			for (const property in properties) {
				const propertyName = properties[property];
				current[propertyName].value = obj[propertyName];

				// current[propertyName].options = [{
				// 	...current[propertyName]
				// }];
			}
			current.time = moment.utc(obj.time);
			WeatherStore.resultCurrent[key].set(current);
		},
		setCurrentWeatherBasedOnTime: (time, updateSlider = false) => {
			const results = get(WeatherStore.results.weather);
			const current = get(WeatherStore.resultCurrent.weather);
			const properties = WeatherStore.meta.weather.properties;

			if (!results.hours) {
				// cannot update until results are in.
				return;
			}

			let index = 0;
			// We want to get the latest weather info that is not past the "time" param.
			const nowTime = moment.utc(time);
			for (let i = 0; i < results.hours.length; i++) {
				const thisTime = moment.utc(results.hours[i].time);
				if (thisTime <= nowTime) {
					index = i;
					continue;
				}
				break;
			}
			// index = results.hours.length - 1;
			// console.log("using weather data item index", index);

			const obj = results.hours[index];
			for (const property in properties) {
				WeatherStore.actions.setPropertyInCurrentWeather(obj, properties[property]);
			}
			current.time = moment.utc(obj.time);

			// WeatherStore.results.rangeRing.setArc(current.visibility.value);

			if (updateSlider) {
				WeatherStore.timeSliderData.value.set(moment(new Date(results.hours[index].time)));
			}
			// console.log(current);
		},
		onResultChanged: () => {
			const results = get(WeatherStore.results.weather);
			const useTime = results.hours[results.hours.length - 1].time;
			WeatherStore.actions.setCurrentBasedOnTime(useTime, true);

			WeatherStore.meta.loadState.set(LoadState.LOADED);
			WeatherStore.meta.error.set("");
			WeatherStore.meta.lastUpdated.set(Date.now());
		},
		submit: () => {
			WeatherStore.helpers.clearError();
			WeatherStore.actions.resetCurrent();

			const lngLatR = get(WeatherStore.query.lngLatReadable);
			if (lngLatR.trim().length === 0) {
				WeatherStore.helpers.setError("Point (lng,lat) must not be left blank.");
				return;
			}
			if (!GeoHelper.isValidLngLat(lngLatR)) {
				WeatherStore.helpers.setError("Point (lng,lat) is formatted incorrectly.");
				return;
			}

			WeatherStore.meta.loadState.set(LoadState.LOADING); // $state = LoadState.LOADING;
			WeatherStore.meta.lastUpdated.set(Date.now()); // $lastUpdated = Date.now();

			const toStormglassTimestamp = (jsDateObject) => {
				// return Math.floor(jsDateObject.valueOf() / 1000);
				return jsDateObject.getUTCFullYear() + "-" +
				StringHelper.numberpad("" + (jsDateObject.getUTCMonth() + 1), 2, "0") + "-" +
				StringHelper.numberpad("" + jsDateObject.getUTCDate(), 2, "0") + "T" +
				StringHelper.numberpad("" + jsDateObject.getUTCHours(), 2, "0") + ":" +
				StringHelper.numberpad("" + jsDateObject.getUTCMinutes(), 2, "0") + ":" +
				StringHelper.numberpad("" + jsDateObject.getUTCSeconds(), 2, "0") + "+00:00";
			};

			const lngLat = get(WeatherStore.query.lngLat);
			const fromOG = get(WeatherStore.query.fromDatetime);
			const from = toStormglassTimestamp(fromOG); // .toISOString();
			// const to = toStormglassTimestamp(get(WeatherStore.query.toDatetime).toDate()); // .toISOString();
			const toOG = new Date(Date.parse(get(WeatherStore.query.toDatetime)) + 86400000);
			const to = toStormglassTimestamp(toOG);

			Analytics.event("query", {
				category: "Environment",
				username: get(AuthStore.userInfo).username,
				lngLat: lngLat,
				from: fromOG.toISOString(),
				to: toOG.toISOString()
			});

			console.log("from", from);
			console.log("to", to);

			API.get("weather/point" +
					"?lng=" + encodeURIComponent(lngLat.lng) +
					"&lat=" + encodeURIComponent(lngLat.lat) +
					"&timestamp_after=" + encodeURIComponent(from) +
					"&timestamp_before=" + encodeURIComponent(to)
			)
				.then((newResults) => {
					WeatherStore.results.weather.set(newResults.weather);
					WeatherStore.results.astronomy.set(newResults.astronomy);
					WeatherStore.results.tidalExtremes.set(newResults.tidal_extremes);
					WeatherStore.results.elevation.set(newResults.elevation);
					WeatherStore.actions.onResultChanged();
				})
				.catch((error) => {
					console.error(error);
					WeatherStore.helpers.setError(error.data.detail);
				});
		}
	},
	helpers: {
		setError: (str) => {
			WeatherStore.meta.loadState.set(LoadState.INIT);
			WeatherStore.meta.error.set(str);
			WeatherStore.meta.lastUpdated.set(Date.now());
		},
		clearError: () => {
			WeatherStore.meta.error.set("");
			WeatherStore.meta.lastUpdated.set(Date.now());
		},
		shouldShowLoadingCard: () => {
			return get(WeatherStore.meta.loadState) === LoadState.LOADING &&
					get(WeatherStore.meta.progress) > 0.0;
		},
		shouldShowLoadingSpinner: () => {
			return get(WeatherStore.meta.loadState) === LoadState.LOADING &&
				get(WeatherStore.meta.error) === "";
		},
		getSourceAcronym: (name) => {
			if (name === "") {
				return "";
			}
			const sources = Object.keys(WeatherDataSource);
			for (let i = 0; i < sources.length; i++) {
				if (WeatherDataSource[sources[i]] === name) {
					return sources[i];
				}
			}
			return "";
		},
		formatValue: (value, unit, transformFunc = null) => {
			if (value === "N/A") {
				return value;
			}
			else if (transformFunc !== null) {
				return transformFunc(value) + "" + unit;
			}
			return value + unit;
		},
		formatTideValue: (value) => {
			let str = get(WeatherStore.results.tidalExtremes).meta.datum;
			if (value > 0) {
				str += " + " + WeatherStore.helpers.formatValue(roundFloat(value, 2), " mtrs");
			}
			else {
				str += " - " + WeatherStore.helpers.formatValue(roundFloat(Math.abs(value), 2), " mtrs");
			}
			return str;
		},
		getMiscForTime: (time, dataset) => {
			let index = 0;
			const nowTime = moment.utc(time);
			for (let i = 0; i < dataset.length; i++) {
				const thisTime = moment.utc(dataset[i].time);
				if (thisTime <= nowTime) {
					index = i;
					continue;
				}
				break;
			}
			// console.log("using " + key + " data item index", index);
			return dataset[index];
		}

	}

};
export default WeatherStore;
